import type { Theme, SxProps, CSSObject } from '@mui/material/styles';

import Box from '@mui/material/Box';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';

// ----------------------------------------------------------------------

const visuallyHidden: CSSObject = {
  border: 0,
  padding: 0,
  width: '1px',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  clip: 'rect(0 0 0 0)',
};

// ----------------------------------------------------------------------

export type TableHeadCellProps = {
  field: string;
  title?: string;
  width?: CSSObject['width'];
  align?: 'left' | 'center' | 'right';
  sx?: SxProps<Theme>;
  /** Render a plain header (no sort affordance) even when onSort is provided. */
  disableSort?: boolean;
  /** sortBy value sent to the API when it differs from the display field (e.g. wine_type_icon sorts as wine_type). */
  sortKey?: string;
};

export type TableHeadCustomProps = {
  orderBy?: string;
  rowCount?: number;
  sx?: SxProps<Theme>;
  numSelected?: number;
  order?: 'asc' | 'desc';
  headLabel: TableHeadCellProps[];
  onSort?: (id: string) => void;
  onSelectAllRows?: (checked: boolean) => void;
};

export function TableHeadCustom({
  sx,
  order,
  onSort,
  orderBy,
  headLabel,
  rowCount = 0,
  numSelected = 0,
  onSelectAllRows,
}: TableHeadCustomProps) {
  return (
    <TableHead
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        backgroundColor: (theme) => theme.palette.background.paper,
        ...sx,
      }}
    >
      <TableRow>
        {onSelectAllRows && (
          <TableCell padding="checkbox">
            <Checkbox
              indeterminate={!!numSelected && numSelected < rowCount}
              checked={!!rowCount && numSelected === rowCount}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onSelectAllRows(event.target.checked)
              }
              slotProps={{
                input: {
                  id: `all-row-checkbox`,
                  'aria-label': `All row Checkbox`,
                },
              }}
            />
          </TableCell>
        )}

        {headLabel.map((headCell) => {
          const sortField = headCell.sortKey ?? headCell.field;
          return (
            <TableCell
              key={headCell.field}
              align={headCell.align || 'left'}
              sortDirection={orderBy === sortField ? order : false}
              sx={{
                width: headCell.width,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontWeight: 'bold',
              }}
            >
              {onSort && !headCell.disableSort ? (
                <TableSortLabel
                  hideSortIcon
                  active={orderBy === sortField}
                  direction={orderBy === sortField ? order : 'asc'}
                  onClick={() => onSort(sortField)}
                >
                  {headCell.title}

                  {orderBy === sortField ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              ) : (
                headCell.title
              )}
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}
